import AutocompleteComponent from 'components/design-system/Autocomplete/Autocomplete';
import ButtonComponent from 'components/design-system/Button/Button';
import Heading from 'components/design-system/Heading/Heading';
import InputComponent from 'components/design-system/input/Input';
import LabelComponent from 'components/design-system/Label/Label';
import Text from 'components/design-system/Text/Text';
import MapAddress from 'components/MapAddress/MapAddress';
import { DICTONARY } from 'const/Dictonary';
// Hooks
import { useDireccion } from 'hooks/useDireccion';
// Material
import { GpsOff, MyLocation } from '@mui/icons-material';
import { useEffect, useMemo } from 'react';

/**
 * SELECTOR DE DIRECCIÓN
 *  {*} obligatorio
 * @param {*} values  // Valores del formulario
 * @param {*} errors  // Errores del formulario
 * @param {*} touched // Campos tocados del formulario
 * @param {*} onChange // Funcion de cambio de formulario
 * @param {*} handleBlur // Funcion de blur de formulario
 * @param {*} setFieldValue // Funcion de seteo de valores
 * @param {*} setFieldTouched // Funcion de seteo de campos tocados
 * @param {*} isSubmitting  // Estado de envio de formulario
 * @param {} nameRegion  // Nombre del campo region
 * @param {} nameComuna // Nombre del campo comuna
 * @param {} nameNombreVia // Nombre del campo nombreVia
 * @param {} nameLatitud // Nombre del campo latitudDireccion
 * @param {} nameLongitud // Nombre del campo longitudDireccion
 * @param {} esOpcionalTitulo // Es opcional el campo titulo - label
 * @param {} esOpcionalRegion // Es opcional el campo region - label
 * @param {} esOpcionalComuna // Es opcional el campo comuna - label
 * @param {} esOpcionalDireccion // Es opcional el campo direccion - label
 * @param {} className // Clase del contenedor principal
 * @param {} classNameTitulo // Clase del titulo
 * @param {} classNameRegion // Clase del campo region
 * @param {} classNameComuna // Clase del campo comuna
 * @param {} classNameDireccion // Clase del campo direccion
 * @param {} classNameMapa  // Clase del mapa
 * @param {} titulo // Texto del titulo
 * @param {} subtitulo // Texto del subtitulo -
 * @returns
 */
const SelectorDireccionContainer = ({
  values,
  errors,
  touched,
  onChange,
  handleBlur,
  setFieldValue,
  setFieldTouched,
  isSubmitting,
  nameRegion = 'region',
  nameComuna = 'comuna',
  nameNombreVia = 'nombreVia',
  nameLatitud = 'latitudDireccion',
  nameLongitud = 'longitudDireccion',
  esOpcionalTitulo = false,
  esOpcionalRegion = false,
  esOpcionalComuna = false,
  esOpcionalDireccion = false,
  className = 'grid grid-cols-12 gap-4',
  classNameTitulo = 'col-span-12',
  classNameRegion = 'col-span-12 xs:col-span-6 lg:col-span-3',
  classNameComuna = 'col-span-12 xs:col-span-6 lg:col-span-3',
  classNameDireccion = 'col-span-12 xs:col-span-12 lg:col-span-6',
  classNameMapa = 'col-span-12 xxs:col-span-12 xs:col-span-12',
  titulo = 'Dirección',
  subtitulo = 'Considera que la dirección que ingreses será usada para entregarte resultados basados en tu ubicación',
  ...props
}) => {
  const {
    latLon,
    setLatLon,
    addressQuerysearch,
    addressQueryParams,
    regiones,
    cargaListaRegiones,
    handleObtenerDireccion,
    handleObtenerComunas,
    listadoComunas,
    cargaListaComunas,
    setBuscandoDireccion,
    buscandoDireccion
  } = useDireccion();
  //  Servicio Comunas
  useMemo(() => {
    handleObtenerComunas(values[nameRegion]?.codigoRegion || null);
  }, [handleObtenerComunas, nameRegion, values]);

  // Estados de carga de servicios
  useMemo(() => {
    if (isSubmitting) return;
    handleObtenerDireccion({
      nombreVia: values[nameNombreVia],
      comuna: values[nameComuna],
      region: values[nameRegion]
    });
  }, [
    isSubmitting,
    handleObtenerDireccion,
    values,
    nameNombreVia,
    nameComuna,
    nameRegion
  ]);

  // Verificacion de cambios en los campos de direccion
  useEffect(() => {
    const { isPointEditing } = values;

    if (isSubmitting || !latLon) return;

    const hasNombreVia = touched[nameNombreVia];
    const hasComuna = touched[nameComuna];
    const hasRegion = touched[nameRegion];

    if (
      hasNombreVia ||
      hasComuna ||
      hasRegion ||
      isPointEditing ||
      values[nameLatitud] !== latLon[0] ||
      values[nameLongitud] !== latLon[1]
    ) {
      setBuscandoDireccion(true);
      setFieldValue(nameLatitud, latLon[0]);
      setFieldValue(nameLongitud, latLon[1]);
    }
  }, [
    isSubmitting,
    latLon,
    setBuscandoDireccion,
    setFieldValue,
    values.isPointEditing,
    values.latitudDireccion,
    values.longitudDireccion,
    values,
    touched,
    nameComuna,
    nameRegion,
    nameNombreVia,
    nameLatitud,
    nameLongitud
  ]);

  return (
    <div className="col-span-12">
      <div className={className}>
        {/* Titulo */}
        <div className={classNameTitulo}>
          <Heading type="h4" className="mb-0 flex gap-2 items-center">
            {titulo}{' '}
            {esOpcionalTitulo && (
              <Text className="text-uv-secondary-0">(Opcional)</Text>
            )}
          </Heading>
          <Text>{subtitulo}</Text>
        </div>
        {/* Region */}
        <div className={classNameRegion}>
          <AutocompleteComponent
            name={nameRegion}
            clearOnEscape={true}
            accesor="nombreRegion"
            openOnFocus={true}
            options={regiones}
            disabled={!cargaListaRegiones}
            onChange={(ev, region, reason) => {
              let data = { name: nameRegion, value: region };
              if (reason === 'clear') {
                data.value = null;
                setFieldValue(nameComuna, null);
              }
              setFieldValue(nameComuna, null);
              setFieldValue(nameRegion, data.value);
            }}
            onBlur={() => setFieldTouched(nameRegion)}
            value={values[nameRegion]}
            getOptionLabel={option => option.nombreRegion || ''}
            isOptionEqualToValue={(option, value) =>
              option.nombreRegion === values[nameRegion]?.nombreRegion
            }
          >
            <InputComponent
              autoComplete={'off'}
              fullWidth
              touched={touched[nameRegion] ? true : false}
              estado={
                errors[nameRegion]
                  ? { tipo: 'error', mensaje: errors[nameRegion] }
                  : null
              }
              label={
                <LabelComponent esOpcional={esOpcionalRegion}>
                  Región
                </LabelComponent>
              }
            />
          </AutocompleteComponent>
        </div>
        {/* Comuna */}
        <div className={classNameComuna}>
          <AutocompleteComponent
            name={nameComuna}
            accesor="nombreComuna"
            options={listadoComunas}
            disabled={!cargaListaComunas}
            onChange={(ev, comuna, reason) => {
              let data = { name: nameComuna, value: comuna };
              if (reason === 'clear') {
                setFieldValue(nameRegion, null);
                setFieldValue(nameComuna, null);
              } else {
                setFieldValue(nameComuna, data.value);
                setFieldValue(nameRegion, data.value.region);
              }
            }}
            value={values[nameComuna]}
            getOptionLabel={option => option.nombreComuna || ''}
            isOptionEqualToValue={(option, value) =>
              option.nombreComuna === values[nameComuna]?.nombreComuna
            }
            onBlur={() => setFieldTouched(nameComuna)}
          >
            <InputComponent
              autoComplete={'off'}
              fullWidth
              touched={touched[nameComuna] ? true : false}
              estado={
                errors[nameComuna]
                  ? { tipo: 'error', mensaje: errors[nameComuna] }
                  : null
              }
              label={
                <LabelComponent esOpcional={esOpcionalComuna}>
                  Comuna
                </LabelComponent>
              }
            />
          </AutocompleteComponent>
        </div>
        {/* Dirección */}
        <div className={classNameDireccion}>
          <div className="col-span-6 xxs:col-span-12  xs:col-span-12  lg:col-span-6">
            <InputComponent
              autoComplete={'off'}
              fullWidth
              label={
                <LabelComponent esOpcional={esOpcionalDireccion}>
                  Nombre y número calle
                </LabelComponent>
              }
              name={nameNombreVia}
              onChange={e => {
                onChange(e);
                setFieldTouched(nameRegion);
                setFieldTouched(nameComuna);
                setFieldTouched(nameNombreVia);
              }}
              onBlur={e => {
                handleBlur(e);
              }}
              value={values[nameNombreVia]}
              placeholder="Ej: Providencia, 1091"
              touched={touched[nameNombreVia]}
              estado={
                errors[nameNombreVia]
                  ? { tipo: 'error', mensaje: errors[nameNombreVia] }
                  : null
              }
            />
          </div>
        </div>
        {/* Mapa */}
        <div className={classNameMapa}>
          <div className="grid grid-cols-12 items-center">
            <div className="col-span-12 xl:col-span-9 overflow-hidden rounded">
              <MapAddress
                addressQuerysearch={addressQuerysearch}
                addressQueryParams={addressQueryParams}
                latLon={latLon}
                setLatLon={setLatLon}
                isPointEditing={values.isPointEditing}
                setIsPointEditing={value =>
                  setFieldValue('isPointEditing', value)
                }
                isLoading={isSubmitting}
                buscandoDireccion={buscandoDireccion}
              />
            </div>
            <div className="col-span-12 xl:col-span-3 h-full">
              <div
                className={`${
                  values.isPointEditing ? ' bg-warning-light' : 'bg-info-light '
                } p-5 justify-between rounded items-center h-full`}
              >
                <Text className="mb-4">
                  {values.isPointEditing
                    ? DICTONARY.MAPA_MENSAJES.INFO_DESACTIVAR_EDICION
                    : DICTONARY.MAPA_MENSAJES.INFO_ACTIVAR_EDICION}
                </Text>
                <ButtonComponent
                  className="min-w-[190px] md:min-w-fit mt-4 sm:mt-0"
                  onClick={() => {
                    setFieldValue('isPointEditing', !values.isPointEditing);
                  }}
                >
                  {values.isPointEditing && latLon ? (
                    <>
                      Desactivar edición <GpsOff className="ml-2" />
                    </>
                  ) : (
                    <>
                      Edición manual <MyLocation className="ml-2" />
                    </>
                  )}
                </ButtonComponent>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectorDireccionContainer;
